import {action, computed, makeAutoObservable, observable} from "mobx";
import {inject, injectable} from "inversify";
import {findLast, orderBy} from "lodash";
import {DragUpdate} from "react-beautiful-dnd";
import {ViewController} from "data/types/structure";
import {ConferenceType, GameSteps} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {
	IDivisionSlot,
	ILightPredictionsStore,
} from "data/stores/light_predictions/light_predictions.store";
import type {ISquadsStore, ISquadWithResults} from "data/stores/squads/squads.store";
import type {IStandingsStore} from "data/stores/standings/standings.store";
import {AFC_PICK_STEPS, NFC_PICK_STEPS} from "data/constants";
import type {IUserStore} from "data/stores/user/user.store";

interface IProps {
	step: GameSteps;
}

export interface ILPSliderCardOrderController extends ViewController<IProps> {
	readonly i18n: ILocalizationStore;

	get divisionName(): string;

	get currentDivisionsPrediction(): IDivisionSlot[];

	get squads(): ISquadWithResults[];

	handleOnDragEnd: (result: DragUpdate) => void;

	toNextStep: () => void;
}

@injectable()
export class LPSliderCardOrderController implements ILPSliderCardOrderController {
	@observable private _step: GameSteps = GameSteps.NFC_SEEDING;

	get conferenceType() {
		return this._lpStore.getConferenceByType(this._step);
	}

	get divisionName() {
		return `${this.conferenceType.toLocaleUpperCase()} ${this.i18n.t(
			"navigation.division.seeding",
			"Seeding"
		)}`;
	}

	get currentDivisionsPrediction() {
		return this._lpStore.lightPrediction[this.conferenceType];
	}

	@computed
	get squads() {
		return this._lpStore.lightPrediction[this.conferenceType]
			.filter((prediction) =>
				[...NFC_PICK_STEPS, ...AFC_PICK_STEPS].includes(prediction.type)
			)
			.map(({squadId}) => {
				const standing = findLast(
					orderBy(this._standingsStore.standings, "roundId"),
					(standing) => standing.squadId === squadId
				);
				const squad = this._squadStore.getSquadById(squadId) || {};

				return {
					...standing,
					...squad,
				} as ISquadWithResults;
			});
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.SquadsStore) private _squadStore: ISquadsStore,
		@inject(Bindings.StandingsStore) private _standingsStore: IStandingsStore,
		@inject(Bindings.LightPredictionsStore) private _lpStore: ILightPredictionsStore
	) {
		makeAutoObservable(this);
	}

	init(param: IProps) {
		this._step = param.step;
	}

	@action handleOnDragEnd = (result: DragUpdate) => {
		if (!result.destination) return;

		const predictions = this._lpStore.lightPrediction[this.conferenceType];
		const element = predictions[result.source.index];
		predictions.splice(result.source.index, 1);
		predictions.splice(result.destination.index, 0, element);

		const updatedPredictions = predictions.map((it, index) => {
			return {
				...it,
				position: index + 1,
			};
		});

		this._lpStore.setPredictionsByConference(updatedPredictions, this.conferenceType);
		this._lpStore.setIsPredictionsChanged(true);
		this._lpStore.setIsPredictionsSaved(false);
		this.clearNextPrediction();
	};

	private clearNextPrediction = () => {
		const predictions = this._lpStore.lightPrediction[this.conferenceType];

		predictions.forEach((prediction) => {
			if ([GameSteps.NFC_WILD_CARD, GameSteps.AFC_WILD_CARD].includes(prediction.type)) {
				prediction.squadId = 0;
			}

			return prediction;
		});

		this._lpStore.customSteps[this.conferenceType].wildCard = false;
	};

	toNextStep = () => {
		if (this.conferenceType === ConferenceType.NFC) {
			this._lpStore.setCurrentStep(GameSteps.NFC_WILD_CARD);
			this._lpStore.customSteps.nfc.seeding = true;
		} else {
			this._lpStore.setCurrentStep(GameSteps.AFC_WILD_CARD);
			this._lpStore.customSteps.afc.seeding = true;
		}
	};
}
